// Apex Business Intelligence — Round 14 (Beta Feedback) ADDITIVE
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { jsPDF } from "jspdf";
import { toast } from "./intelligence";

const API = process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem("apex_auth_token") || "";
const j = async (p, init = {}) => {
  const r = await fetch(`${API}${p}`, {
    ...init,
    headers: { ...(init.headers || {}), "Content-Type": "application/json", Authorization: "Bearer " + tok() },
  });
  if (!r.ok) {
    let m = "";
    try { m = (await r.json()).detail || ""; } catch {}
    throw new Error(m || `HTTP ${r.status}`);
  }
  return r.json();
};

// ─── IICRC dry standards lookup ───────────────────────────────────────────
export const IICRC_STANDARDS = {
  "Drywall":          { target: 12, unit: "%", label: "≤ 12%" },
  "Wood Framing":     { target: 19, unit: "%", label: "≤ 19%" },
  "Hardwood Floor":   { target: 12, unit: "%", label: "≤ 12%" },
  "Laminate":         { target: 12, unit: "%", label: "≤ 12%" },
  "Carpet":           { target: 0,  unit: "%", label: "Dry to touch" },
  "Subfloor OSB":     { target: 15, unit: "%", label: "≤ 15%" },
  "Concrete":         { target: 4,  unit: "%", label: "Varies by slab" },
  "Cabinets":         { target: 16, unit: "%", label: "≤ 16%" },
  "Baseboard":        { target: 12, unit: "%", label: "≤ 12%" },
};
export const MATERIAL_TYPES = Object.keys(IICRC_STANDARDS);

function statusFor(material, reading) {
  if (reading == null || reading === "") return { label: "—", color: "#9ca3af", bg: "#f3f4f6" };
  const std = IICRC_STANDARDS[material];
  if (!std) return { label: "—", color: "#9ca3af", bg: "#f3f4f6" };
  const r = parseFloat(reading);
  if (Number.isNaN(r)) return { label: "—", color: "#9ca3af", bg: "#f3f4f6" };
  const t = std.target;
  if (r >= 25) return { label: "CRITICAL", color: "#fff", bg: "#7f1d1d" };
  if (r > t + 3) return { label: "WET", color: "#fff", bg: "#dc2626" };
  if (r > t) return { label: "CLOSE", color: "#7c2d12", bg: "#fef3c7" };
  return { label: "DRY", color: "#065f46", bg: "#d1fae5" };
}

// ─── Psychrometric calc helpers ────────────────────────────────────────────
function computePsychro(p) {
  const T = parseFloat(p.indoor_temp_f), RH = parseFloat(p.indoor_rh);
  const T2 = parseFloat(p.outdoor_temp_f), RH2 = parseFloat(p.outdoor_rh);
  const aham = parseFloat(p.aham_rating), pints = parseFloat(p.actual_pints_removed);
  const out = { ...p };
  // Dew point (Magnus) °F
  const dp = (Tf, rh) => {
    if (Number.isNaN(Tf) || Number.isNaN(rh)) return null;
    const Tc = (Tf - 32) * 5 / 9;
    const a = 17.27, b = 237.7;
    const alpha = (a * Tc) / (b + Tc) + Math.log(rh / 100);
    const dpC = (b * alpha) / (a - alpha);
    return Math.round((dpC * 9 / 5 + 32) * 10) / 10;
  };
  // GPP (grains per pound) — approximate
  const gpp = (Tf, rh) => {
    if (Number.isNaN(Tf) || Number.isNaN(rh)) return null;
    const Tc = (Tf - 32) * 5 / 9;
    const Pws = 6.1078 * Math.pow(10, (7.5 * Tc) / (237.3 + Tc));
    const Pw = (rh / 100) * Pws;
    const w = 0.62198 * Pw / (1013.25 - Pw);
    return Math.round(w * 7000 * 10) / 10;
  };
  out.dew_point = dp(T, RH);
  out.specific_humidity = gpp(T, RH) ? Math.round(gpp(T, RH) / 70 * 100) / 100 : null;
  out.gpp_indoor = gpp(T, RH);
  out.gpp_outdoor = gpp(T2, RH2);
  out.gpp_diff = (out.gpp_indoor != null && out.gpp_outdoor != null) ? Math.round((out.gpp_indoor - out.gpp_outdoor) * 10) / 10 : null;
  if (!Number.isNaN(aham) && !Number.isNaN(pints) && aham > 0) {
    out.dehu_efficiency_pct = Math.round((pints / aham) * 1000) / 10;
  }
  return out;
}

// ─── DRY READINGS TAB (FIX 1 + 6) ─────────────────────────────────────────
export function DryReadingsTab({ jobId, job }) {
  const [days, setDays] = useState([]);
  const [activeLabel, setActiveLabel] = useState("Day 1");
  const [suggest, setSuggest] = useState(null);
  const [sqFt, setSqFt] = useState(job?.affected_sq_ft || "");
  const [wcat, setWcat] = useState(job?.water_category || "Category 1");
  const [wcls, setWcls] = useState(job?.water_class || "Class 2");

  const load = useCallback(async () => {
    try { const d = await j(`/api/iv/r14/dry/${jobId}`); setDays(d.days || []); } catch { setDays([]); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);
  const requestSuggest = async () => {
    if (!sqFt) { toast("Enter affected sq ft first", "err"); return; }
    try {
      const s = await j(`/api/iv/r14/dry/suggest_equipment?sq_ft=${sqFt}&water_category=${encodeURIComponent(wcat)}&water_class=${encodeURIComponent(wcls)}`);
      setSuggest(s);
    } catch (e) { toast(e.message, "err"); }
  };

  // Build the full ordered tab list: Day 1..Day7, additional days, Final last.
  const tabs = useMemo(() => {
    const base = ["Day 1","Day 2","Day 3","Day 4","Day 5","Day 6","Day 7"];
    const known = new Set(days.map(d => d.day_label));
    base.forEach(b => known.add(b));
    const extras = [...known].filter(l => /^Day \d+$/.test(l) && !base.includes(l))
      .sort((a, b) => parseInt(a.slice(4)) - parseInt(b.slice(4)));
    return [...base, ...extras, "Final"];
  }, [days]);
  const totalDays = tabs.filter(t => t !== "Final").length;
  const activeIdx = tabs.indexOf(activeLabel);

  const addDay = () => {
    const max = tabs.filter(t => /^Day \d+$/.test(t)).reduce((m, t) => Math.max(m, parseInt(t.slice(4))), 7);
    const next = `Day ${max + 1}`;
    setDays(prev => [...prev, { day_label: next, day_order: max + 1, rows: [], equipment_log: [], psychro: {}, _dirty: true }]);
    setActiveLabel(next);
  };
  const removeDay = async (label) => {
    if (!window.confirm(`Remove ${label}? Only allowed when no data is saved on it.`)) return;
    const found = days.find(d => d.day_label === label);
    if (found && found.id) {
      try { await j(`/api/iv/r14/dry/${jobId}/${encodeURIComponent(label)}`, { method: "DELETE" }); } catch {}
    }
    setDays(prev => prev.filter(d => d.day_label !== label));
    if (activeLabel === label) setActiveLabel("Day 1");
  };

  const dayDoc = days.find(d => d.day_label === activeLabel) || { day_label: activeLabel, day_order: activeLabel === "Final" ? 9999 : _ord(activeLabel), rows: [], equipment_log: [], psychro: {} };

  return (
    <div data-testid="r14-dry-tab" style={{ color: "#1a1a1a" }}>
      <div style={{ fontSize: 12, color: "#1a1a1a", fontWeight: 700, marginBottom: 6 }}>
        Drying Log — {activeLabel} of {totalDays} total day{totalDays === 1 ? "" : "s"}
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12, padding: "6px 0", borderBottom: "1px solid #e5e7eb" }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveLabel(t)} data-testid={`r14-dry-tab-${t.replace(/\s/g, "-")}`}
            style={{ padding: "4px 10px", fontSize: 11, fontWeight: 600,
                     border: "1px solid #d1d5db", borderRadius: 4, cursor: "pointer",
                     background: activeLabel === t ? "#C0392B" : "#fff",
                     color: activeLabel === t ? "#fff" : "#1a1a1a" }}>{t}</button>
        ))}
        <button onClick={addDay} data-testid="r14-dry-add-day"
          style={{ padding: "4px 10px", fontSize: 11, fontWeight: 700, background: "#1e40af", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", marginLeft: 8 }}>+ Add Additional Day</button>
      </div>
      <DayPanel jobId={jobId} dayDoc={dayDoc} onSaved={load} onRemove={() => removeDay(activeLabel)} canRemove={activeIdx >= 7 && tabs[activeIdx] !== "Final" && !dayDoc.id} />
      <div style={{ marginTop: 14, padding: "10px 12px", border: "1px dashed #c7d2fe", borderRadius: 6, background: "#f8faff" }}>
        <div style={{ fontSize: 12, color: "#1a1a1a", fontWeight: 700, marginBottom: 6 }}>Equipment Suggestion (IICRC S500)</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Field label="Affected sq ft"><input style={inDark} type="number" value={sqFt} onChange={e => setSqFt(e.target.value)} data-testid="r14-suggest-sqft" /></Field>
          <Field label="Water Category">
            <select style={inDark} value={wcat} onChange={e => setWcat(e.target.value)} data-testid="r14-suggest-cat">
              {["Category 1", "Category 2", "Category 3"].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Water Class">
            <select style={inDark} value={wcls} onChange={e => setWcls(e.target.value)} data-testid="r14-suggest-cls">
              {["Class 1", "Class 2", "Class 3", "Class 4"].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <button onClick={requestSuggest} className="btn btn-primary" style={{ fontSize: 11 }} data-testid="r14-suggest-btn">Suggest Equipment</button>
        </div>
        {suggest && <div data-testid="r14-suggest-note" style={{ marginTop: 8, padding: "10px 12px", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 4, fontSize: 12, color: "#7c2d12", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>{suggest.note}</span>
          <button onClick={async () => {
            try {
              // Fetch existing Day 1 (if any), append suggested rows without overwriting existing entries
              let day1 = days.find(d => d.day_label === "Day 1");
              const existing = (day1?.equipment_log) || [];
              const newRows = [];
              if (suggest.air_movers > 0) newRows.push({ equip_type: "Air Mover", quantity: suggest.air_movers, make_model: "", serial: "", placement: "", capacity: "", hours: 0, notes: "Auto-suggested (IICRC)" });
              if (suggest.dehumidifiers > 0) newRows.push({ equip_type: `Dehumidifier (${suggest.dehu_type})`, quantity: suggest.dehumidifiers, make_model: "", serial: "", placement: "", capacity: "", hours: 0, notes: "Auto-suggested (IICRC)" });
              if (suggest.air_scrubbers > 0) newRows.push({ equip_type: "Air Scrubber", quantity: suggest.air_scrubbers, make_model: "", serial: "", placement: "", capacity: "", hours: 0, notes: "Auto-suggested (IICRC)" });
              const merged = [...existing, ...newRows];
              const payload = {
                job_id: jobId,
                day_label: "Day 1",
                day_order: 1,
                reading_at: day1?.reading_at || "",
                technician: day1?.technician || "",
                overall_pct_dryness: day1?.overall_pct_dryness ?? null,
                rows: day1?.rows || [],
                psychro: day1?.psychro || {},
                equipment_log: merged,
                detailed_notes: day1?.detailed_notes || "",
              };
              await j("/api/iv/r14/dry", { method: "POST", body: JSON.stringify(payload) });
              toast("Suggested equipment added to Day 1 log.");
              setActiveLabel("Day 1");
              load();
            } catch (e) { toast(e.message || "Could not apply", "err"); }
          }} className="btn btn-primary" style={{ fontSize: 11, whiteSpace: "nowrap" }} data-testid="r14-suggest-apply">Apply Suggestion to Equipment Log</button>
        </div>}
      </div>
    </div>
  );
}

function _ord(label) { try { return parseInt(label.replace("Day", "").trim()); } catch { return 1; } }

const lblDark = { fontSize: 10, fontWeight: 700, color: "#1a1a1a", marginBottom: 4, display: "block", letterSpacing: 0.3 };
const inDark = { width: "100%", padding: "6px 8px", border: "1px solid #c8c8c8", borderRadius: 4, fontSize: 12, color: "#1a1a1a", fontWeight: 500, background: "#fff" };
const subDark = { fontFamily: "Rajdhani,sans-serif", fontSize: 13, fontWeight: 700, marginTop: 14, marginBottom: 8, letterSpacing: 0.5, color: "#1a1a1a" };

function Field({ label, children, ...rest }) {
  return <div style={{ flex: 1, minWidth: 110, ...rest }}><label style={lblDark}>{label}</label>{children}</div>;
}

function DayPanel({ jobId, dayDoc, onSaved, onRemove, canRemove }) {
  const [doc, setDoc] = useState(dayDoc);
  useEffect(() => { setDoc(dayDoc); }, [dayDoc.day_label, dayDoc.id]);
  const upd = (k, v) => setDoc(d => ({ ...d, [k]: v }));
  const updPsychro = (k, v) => setDoc(d => {
    const next = { ...(d.psychro || {}), [k]: v };
    const calc = computePsychro(next);
    return { ...d, psychro: calc };
  });
  const addRow = () => upd("rows", [...(doc.rows || []), { id: "tmp-" + Date.now(), material_type: "Drywall", iicrc_standard: IICRC_STANDARDS["Drywall"].label, location: "", reading_pct: "" }]);
  const updRow = (i, patch) => upd("rows", doc.rows.map((r, idx) => idx === i ? { ...r, ...patch, ...(patch.material_type ? { iicrc_standard: IICRC_STANDARDS[patch.material_type]?.label || "" } : {}) } : r));
  const delRow = (i) => upd("rows", doc.rows.filter((_, idx) => idx !== i));
  const addEquip = () => upd("equipment_log", [...(doc.equipment_log || []), { id: "tmp-" + Date.now(), equip_type: "Air Mover", make_model: "", serial: "", quantity: 1, placement: "", capacity: "", hours: 0, notes: "" }]);
  const updEquip = (i, patch) => upd("equipment_log", doc.equipment_log.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const delEquip = (i) => upd("equipment_log", doc.equipment_log.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      const payload = { ...doc, job_id: jobId };
      delete payload._dirty;
      await j("/api/iv/r14/dry", { method: "POST", body: JSON.stringify(payload) });
      toast(`${doc.day_label} saved`);
      onSaved && onSaved();
    } catch (e) { toast(e.message, "err"); }
  };

  return (
    <div data-testid={`r14-dry-panel-${doc.day_label.replace(/\s/g, "-")}`}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <Field label="Date and Time"><input type="datetime-local" style={inDark} value={(doc.reading_at || "").slice(0, 16)} onChange={e => upd("reading_at", e.target.value)} data-testid="r14-day-when" /></Field>
        <Field label="Technician"><input style={inDark} value={doc.technician || ""} onChange={e => upd("technician", e.target.value)} placeholder="e.g. Mike Rodriguez" data-testid="r14-day-tech" /></Field>
        <Field label="Overall % Dryness"><input style={inDark} type="number" min={0} max={100} value={doc.overall_pct_dryness ?? ""} onChange={e => upd("overall_pct_dryness", e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="r14-day-overall" /></Field>
        <div style={{ alignSelf: "flex-end" }}>
          <button onClick={save} className="btn btn-primary" style={{ fontSize: 11 }} data-testid="r14-day-save">Save {doc.day_label}</button>
          {canRemove && <button onClick={onRemove} style={{ fontSize: 11, marginLeft: 6, padding: "5px 10px", background: "transparent", color: "#C0392B", border: "1px solid #C0392B", borderRadius: 4, cursor: "pointer", fontWeight: 600 }} data-testid="r14-day-remove">Remove Day</button>}
        </div>
      </div>

      <div style={subDark}>Moisture Readings</div>
      <table style={{ width: "100%", fontSize: 11 }}>
        <thead><tr>
          <th style={th}>Location / Room</th><th style={th}>Material</th><th style={th}>Reading %</th><th style={th}>IICRC Standard</th><th style={th}>Status</th><th></th>
        </tr></thead>
        <tbody>
          {(doc.rows || []).map((r, i) => {
            const st = statusFor(r.material_type, r.reading_pct);
            return (
              <tr key={r.id || i}>
                <td style={td}><input style={inDark} value={r.location || ""} onChange={e => updRow(i, { location: e.target.value })} data-testid={`r14-row-loc-${i}`} /></td>
                <td style={td}>
                  <select style={inDark} value={r.material_type || ""} onChange={e => updRow(i, { material_type: e.target.value })} data-testid={`r14-row-mat-${i}`}>
                    {MATERIAL_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </td>
                <td style={td}><input type="number" step="0.1" style={inDark} value={r.reading_pct ?? ""} onChange={e => updRow(i, { reading_pct: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`r14-row-pct-${i}`} /></td>
                <td style={{ ...td, fontWeight: 600 }}>{IICRC_STANDARDS[r.material_type]?.label || "—"}</td>
                <td style={td}><span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, color: st.color, background: st.bg }} data-testid={`r14-row-status-${i}`}>{st.label}</span></td>
                <td style={td}><button onClick={() => delRow(i)} style={delBtn}>×</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button onClick={addRow} className="btn btn-outline" style={{ fontSize: 11, marginTop: 6 }} data-testid="r14-row-add">+ Add Row</button>

      <div style={subDark}>Psychrometric Data</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
        <Field label="Indoor Temp °F"><input style={inDark} type="number" value={doc.psychro?.indoor_temp_f ?? ""} onChange={e => updPsychro("indoor_temp_f", e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="r14-psy-it" /></Field>
        <Field label="Indoor RH %"><input style={inDark} type="number" value={doc.psychro?.indoor_rh ?? ""} onChange={e => updPsychro("indoor_rh", e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="r14-psy-irh" /></Field>
        <Field label="Outdoor Temp °F"><input style={inDark} type="number" value={doc.psychro?.outdoor_temp_f ?? ""} onChange={e => updPsychro("outdoor_temp_f", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
        <Field label="Outdoor RH %"><input style={inDark} type="number" value={doc.psychro?.outdoor_rh ?? ""} onChange={e => updPsychro("outdoor_rh", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
        <Field label="GPP Indoor (calc)"><input style={{ ...inDark, background: "#f9fafb" }} value={doc.psychro?.gpp_indoor ?? ""} readOnly data-testid="r14-psy-gpp-in" /></Field>
        <Field label="GPP Outdoor (calc)"><input style={{ ...inDark, background: "#f9fafb" }} value={doc.psychro?.gpp_outdoor ?? ""} readOnly /></Field>
        <Field label="Dew Point (calc)"><input style={{ ...inDark, background: "#f9fafb" }} value={doc.psychro?.dew_point ?? ""} readOnly /></Field>
        <Field label="Specific Humidity (calc)"><input style={{ ...inDark, background: "#f9fafb" }} value={doc.psychro?.specific_humidity ?? ""} readOnly /></Field>
        <Field label="GPP Diff (in − out)"><input style={{ ...inDark, background: "#f9fafb" }} value={doc.psychro?.gpp_diff ?? ""} readOnly data-testid="r14-psy-gpp-diff" /></Field>
        <Field label="AHAM Rating (dehu)"><input style={inDark} type="number" value={doc.psychro?.aham_rating ?? ""} onChange={e => updPsychro("aham_rating", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
        <Field label="Actual Pints Removed"><input style={inDark} type="number" value={doc.psychro?.actual_pints_removed ?? ""} onChange={e => updPsychro("actual_pints_removed", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
        <Field label="Dehu Efficiency %"><input style={{ ...inDark, background: "#f9fafb" }} value={doc.psychro?.dehu_efficiency_pct ?? ""} readOnly /></Field>
      </div>
      {doc.psychro?.gpp_diff != null && (
        <div data-testid="r14-psy-status" style={{ marginTop: 8, padding: "8px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700,
              background: doc.psychro.gpp_diff > 0 ? "#d1fae5" : "#fef3c7",
              color: doc.psychro.gpp_diff > 0 ? "#065f46" : "#7c2d12" }}>
          {doc.psychro.gpp_diff > 0 ? "Conditions favor drying — indoor GPP higher than outdoor." : "Close windows — indoor GPP lower than outdoor."}
        </div>
      )}

      <div style={subDark}>Equipment Log</div>
      <table style={{ width: "100%", fontSize: 11 }}>
        <thead><tr>
          <th style={th}>Type</th><th style={th}>Make/Model</th><th style={th}>Serial</th><th style={th}>Qty</th><th style={th}>Placement</th><th style={th}>CFM / Pints</th><th style={th}>Hours</th><th style={th}>Notes</th><th></th>
        </tr></thead>
        <tbody>
          {(doc.equipment_log || []).map((r, i) => (
            <tr key={r.id || i}>
              <td style={td}><input style={inDark} value={r.equip_type || ""} onChange={e => updEquip(i, { equip_type: e.target.value })} /></td>
              <td style={td}><input style={inDark} value={r.make_model || ""} onChange={e => updEquip(i, { make_model: e.target.value })} /></td>
              <td style={td}><input style={inDark} value={r.serial || ""} onChange={e => updEquip(i, { serial: e.target.value })} /></td>
              <td style={td}><input style={inDark} type="number" value={r.quantity || 1} onChange={e => updEquip(i, { quantity: parseInt(e.target.value) || 1 })} /></td>
              <td style={td}><input style={inDark} value={r.placement || ""} onChange={e => updEquip(i, { placement: e.target.value })} /></td>
              <td style={td}><input style={inDark} value={r.capacity || ""} onChange={e => updEquip(i, { capacity: e.target.value })} /></td>
              <td style={td}><input style={inDark} type="number" value={r.hours ?? 0} onChange={e => updEquip(i, { hours: parseFloat(e.target.value) || 0 })} /></td>
              <td style={td}><input style={inDark} value={r.notes || ""} onChange={e => updEquip(i, { notes: e.target.value })} /></td>
              <td style={td}><button onClick={() => delEquip(i)} style={delBtn}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addEquip} className="btn btn-outline" style={{ fontSize: 11, marginTop: 6 }} data-testid="r14-equip-add">+ Add Equipment Row</button>

      <div style={subDark}>Detailed Notes</div>
      <textarea style={{ ...inDark, minHeight: 70, fontFamily: "inherit" }} value={doc.detailed_notes || ""} onChange={e => upd("detailed_notes", e.target.value)} placeholder="Daily notes — auto-timestamped on save…" data-testid="r14-day-notes" />

      <div style={subDark}>Drying Standards Comparison</div>
      <table style={{ width: "100%", fontSize: 11 }}>
        <thead><tr>
          <th style={th}>Material</th><th style={th}>Target</th><th style={th}>Reading</th><th style={th}>Variance</th><th style={th}>Status</th>
        </tr></thead>
        <tbody>
          {(doc.rows || []).filter(r => r.material_type).map((r, i) => {
            const std = IICRC_STANDARDS[r.material_type];
            const tgt = std?.target ?? 0;
            const reading = parseFloat(r.reading_pct);
            const variance = Number.isNaN(reading) ? "—" : (reading - tgt).toFixed(1);
            const st = statusFor(r.material_type, r.reading_pct);
            return (
              <tr key={i}>
                <td style={td}>{r.material_type}</td>
                <td style={td}>{std?.label || "—"}</td>
                <td style={td}>{r.reading_pct ?? "—"}%</td>
                <td style={td}>{variance}</td>
                <td style={td}><span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, color: st.color, background: st.bg }}>{st.label}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const th = { textAlign: "left", padding: "6px 6px", color: "#1a1a1a", fontWeight: 700, borderBottom: "1px solid #e5e7eb", fontSize: 10 };
const td = { padding: "4px 6px", color: "#1a1a1a", borderBottom: "1px solid #f3f4f6" };
const delBtn = { background: "transparent", border: "1px solid #C0392B", color: "#C0392B", borderRadius: 3, padding: "2px 6px", fontSize: 11, cursor: "pointer", fontWeight: 700 };

// ─── REPAIRS TAB (FIX 5) ──────────────────────────────────────────────────
const REPAIR_CATS = [
  { key: "drywall",   label: "Drywall Repair",          fields: [["sq_ft", "Sq Ft"], ["sheets", "# Sheets"]] },
  { key: "baseboard", label: "Baseboard Replacement",   fields: [["linear_ft", "Linear Ft"], ["material_type", "Material Type"]] },
  { key: "flooring",  label: "Flooring Repair / Replace", fields: [["sq_ft", "Sq Ft"], ["material_type", "Material Type"]] },
  { key: "paint",     label: "Paint",                   fields: [["sq_ft", "Sq Ft"], ["coats", "# Coats"], ["paint_type", "Paint Type"]] },
  { key: "mold",      label: "Mold Remediation",        fields: [["sq_ft", "Sq Ft Affected"], ["containment", "Containment? (Y/N)"], ["hepa", "HEPA? (Y/N)"], ["antimicrobial", "Antimicrobial? (Y/N)"], ["bags", "Disposal Bags"], ["clearance", "Clearance Test? (Y/N)"]] },
  { key: "custom",    label: "Custom Line",             fields: [["qty", "Quantity"], ["unit_cost", "Unit Cost"]] },
];

export function RepairsTab({ jobId }) {
  const [list, setList] = useState([]);
  const [cat, setCat] = useState("drywall");
  const [draft, setDraft] = useState({});
  const load = useCallback(async () => {
    try { const d = await j(`/api/iv/r14/repairs/${jobId}`); setList(d.repairs || []); } catch { setList([]); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await j("/api/iv/r14/repairs", { method: "POST", body: JSON.stringify({ ...draft, job_id: jobId, category: cat }) });
      toast("Repair added");
      setDraft({});
      load();
    } catch (e) { toast(e.message, "err"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this repair line?")) return;
    await j(`/api/iv/r14/repairs/${id}`, { method: "DELETE" });
    load();
  };

  const grand = list.reduce((a, r) => a + (parseFloat(r.labor_cost) || 0) + (parseFloat(r.material_cost) || 0), 0);
  const cur = REPAIR_CATS.find(c => c.key === cat);

  return (
    <div data-testid="r14-repairs-tab" style={{ color: "#1a1a1a" }}>
      <div style={subDark}>Add Repair Line</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Field label="Category">
          <select style={inDark} value={cat} onChange={e => { setCat(e.target.value); setDraft({}); }} data-testid="r14-rep-cat">
            {REPAIR_CATS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </Field>
        {cat === "custom" && <Field label="Description"><input style={inDark} value={draft.description || ""} onChange={e => setDraft({ ...draft, description: e.target.value })} /></Field>}
        {(cur?.fields || []).map(([k, lbl]) => (
          <Field key={k} label={lbl}>
            <input style={inDark} value={(draft.fields || {})[k] || ""} onChange={e => setDraft({ ...draft, fields: { ...(draft.fields || {}), [k]: e.target.value } })} data-testid={`r14-rep-fld-${k}`} />
          </Field>
        ))}
        <Field label="Labor Hours"><input style={inDark} type="number" value={draft.labor_hours || ""} onChange={e => setDraft({ ...draft, labor_hours: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Labor Cost $"><input style={inDark} type="number" value={draft.labor_cost || ""} onChange={e => setDraft({ ...draft, labor_cost: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Material Cost $"><input style={inDark} type="number" value={draft.material_cost || ""} onChange={e => setDraft({ ...draft, material_cost: parseFloat(e.target.value) || 0 })} /></Field>
        <div style={{ alignSelf: "flex-end" }}><button className="btn btn-primary" onClick={save} style={{ fontSize: 11 }} data-testid="r14-rep-save">+ Add</button></div>
      </div>
      <Field label="Notes"><textarea style={{ ...inDark, minHeight: 50, fontFamily: "inherit" }} value={draft.notes || ""} onChange={e => setDraft({ ...draft, notes: e.target.value })} /></Field>

      <div style={subDark}>All Repair Lines ({list.length}) · Grand Total ${grand.toFixed(2)}</div>
      <table style={{ width: "100%", fontSize: 11 }}>
        <thead><tr>
          <th style={th}>Category</th><th style={th}>Details</th><th style={th}>Labor $</th><th style={th}>Material $</th><th style={th}>Section Total</th><th></th>
        </tr></thead>
        <tbody>
          {list.map(r => {
            const sec = (parseFloat(r.labor_cost) || 0) + (parseFloat(r.material_cost) || 0);
            return (
              <tr key={r.id}>
                <td style={td}>{(REPAIR_CATS.find(c => c.key === r.category) || {}).label || r.category}</td>
                <td style={td}>{r.description || Object.entries(r.fields || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}</td>
                <td style={td}>${(r.labor_cost || 0).toFixed(2)}</td>
                <td style={td}>${(r.material_cost || 0).toFixed(2)}</td>
                <td style={{ ...td, fontWeight: 700 }}>${sec.toFixed(2)}</td>
                <td style={td}><button onClick={() => del(r.id)} style={delBtn}>×</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {list.length > 0 && <div style={{ textAlign: "right", marginTop: 8, fontSize: 13, fontWeight: 700, color: "#1a1a1a" }}>GRAND TOTAL: ${grand.toFixed(2)} · All lines flow to invoice</div>}
    </div>
  );
}

// ─── SKETCH TAB (FIX 4) ───────────────────────────────────────────────────

export function SketchTab({ jobId, job }) {
  const [shapes, setShapes] = useState([]);
  const [tool, setTool] = useState("wall");   // wall | room | dim | affected | label | airmover | dehu | scrubber | moisture
  const [zoom, setZoom] = useState(1);
  const [drawing, setDrawing] = useState(null);
  const svgRef = useRef(null);
  const W = 1200, H = 700;

  const load = useCallback(async () => {
    try { const d = await j(`/api/iv/r14/sketch/${jobId}`); setShapes(d.sketch?.shapes || []); } catch { setShapes([]); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { await j("/api/iv/r14/sketch", { method: "PUT", body: JSON.stringify({ job_id: jobId, shapes, width: W, height: H }) }); toast("Sketch saved"); }
    catch (e) { toast(e.message, "err"); }
  };
  const undo = () => setShapes(s => s.slice(0, -1));
  const clearAll = () => { if (window.confirm("Clear all sketch content?")) setShapes([]); };

  const getPt = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / zoom, y: (e.clientY - r.top) / zoom };
  };
  const onDown = (e) => {
    const p = getPt(e);
    if (["wall", "dim", "affected"].includes(tool)) setDrawing({ tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
    else if (tool === "room") {
      const name = window.prompt("Room name?") || "Room";
      setShapes(s => [...s, { id: Date.now(), tool: "room", x: p.x - 60, y: p.y - 30, w: 120, h: 60, label: name }]);
    } else if (tool === "label") {
      const text = window.prompt("Label text?") || "";
      if (text) setShapes(s => [...s, { id: Date.now(), tool: "label", x: p.x, y: p.y, text }]);
    } else if (["airmover", "dehu", "scrubber", "moisture"].includes(tool)) {
      setShapes(s => [...s, { id: Date.now(), tool, x: p.x, y: p.y }]);
    }
  };
  const onMove = (e) => { if (!drawing) return; const p = getPt(e); setDrawing(d => ({ ...d, x2: p.x, y2: p.y })); };
  const onUp = () => {
    if (!drawing) return;
    let extra = {};
    if (drawing.tool === "dim") {
      const len = window.prompt("Measurement (e.g. 12 ft 6 in)?") || "";
      extra.label = len;
    }
    setShapes(s => [...s, { id: Date.now(), ...drawing, ...extra }]);
    setDrawing(null);
  };

  const icons = { airmover: "AM", dehu: "DH", scrubber: "AS", moisture: "M" };
  const tools = [["wall", "Wall"], ["room", "Room"], ["dim", "Dimension"], ["affected", "Affected Area"], ["label", "Label"], ["airmover", "Air Mover"], ["dehu", "Dehumidifier"], ["scrubber", "Air Scrubber"], ["moisture", "Moisture Pin"]];

  return (
    <div data-testid="r14-sketch-tab" style={{ color: "#1a1a1a" }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8, alignItems: "center" }}>
        {tools.map(([k, l]) => (
          <button key={k} onClick={() => setTool(k)} data-testid={`r14-sketch-tool-${k}`}
            style={{ fontSize: 10, padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 3, cursor: "pointer", fontWeight: 600,
                     background: tool === k ? "#1e40af" : "#fff", color: tool === k ? "#fff" : "#1a1a1a" }}>{l}</button>
        ))}
        <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} style={btn3}>+ Zoom</button>
        <button onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} style={btn3}>- Zoom</button>
        <button onClick={undo} style={btn3} data-testid="r14-sketch-undo">Undo</button>
        <button onClick={clearAll} style={{ ...btn3, color: "#C0392B" }} data-testid="r14-sketch-clear">Clear All</button>
        <button onClick={save} className="btn btn-primary" style={{ fontSize: 11, marginLeft: 6 }} data-testid="r14-sketch-save">Save Sketch</button>
        <button onClick={async () => {
          // R15 — Real PDF export: rasterize the SVG to PNG, embed in jsPDF with job header
          try {
            const svg = svgRef.current;
            if (!svg) { toast("Nothing to export", "err"); return; }
            const xml = new XMLSerializer().serializeToString(svg);
            const svgUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
            const img = new Image();
            await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = svgUrl; });
            const cvs = document.createElement("canvas");
            cvs.width = W; cvs.height = H;
            const ctx = cvs.getContext("2d");
            ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, W, H);
            ctx.drawImage(img, 0, 0, W, H);
            const png = cvs.toDataURL("image/png");
            const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            // Header
            pdf.setFontSize(16); pdf.setFont("helvetica", "bold");
            pdf.text(`Floor Plan Sketch — Job ${jobId}`, 30, 32);
            pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
            const meta = `${job?.customer || ""}${job?.location ? "  ·  " + job.location : ""}  ·  ${new Date().toLocaleString()}`;
            pdf.text(meta, 30, 48);
            // Image — fit while preserving aspect
            const margin = 30; const top = 60;
            const drawW = pageW - margin * 2;
            const drawH = Math.min(pageH - top - margin, drawW * (H / W));
            pdf.addImage(png, "PNG", margin, top, drawW, drawH);
            pdf.setFontSize(8); pdf.setTextColor(120);
            pdf.text("Apex Business Intelligence · Sketch export", margin, pageH - 14);
            pdf.save(`sketch_${jobId}.pdf`);
            toast("Sketch PDF downloaded");
          } catch (e) { toast(e.message || "PDF export failed", "err"); }
        }} style={btn3} data-testid="r14-sketch-export-pdf">Export PDF</button>
      </div>
      <div style={{ border: "1px solid #d1d5db", borderRadius: 4, overflow: "auto", background: "#fff" }}>
        <svg ref={svgRef} width={W * zoom} height={H * zoom} viewBox={`0 0 ${W} ${H}`} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}
             style={{ background: "#f9fafb", cursor: "crosshair" }} data-testid="r14-sketch-svg">
          {/* grid */}
          {Array.from({ length: Math.floor(W / 40) }).map((_, i) => <line key={"vx" + i} x1={i * 40} y1={0} x2={i * 40} y2={H} stroke="#eef" />)}
          {Array.from({ length: Math.floor(H / 40) }).map((_, i) => <line key={"hy" + i} x1={0} y1={i * 40} x2={W} y2={i * 40} stroke="#eef" />)}
          {shapes.map(sh => {
            if (sh.tool === "wall") return <line key={sh.id} x1={sh.x1} y1={sh.y1} x2={sh.x2} y2={sh.y2} stroke="#1a1a1a" strokeWidth={4} />;
            if (sh.tool === "dim") return <g key={sh.id}><line x1={sh.x1} y1={sh.y1} x2={sh.x2} y2={sh.y2} stroke="#1e40af" strokeDasharray="6 4" /><text x={(sh.x1 + sh.x2) / 2} y={(sh.y1 + sh.y2) / 2 - 6} fill="#1e40af" fontSize="13" fontWeight="700">{sh.label || ""}</text></g>;
            if (sh.tool === "affected") return <rect key={sh.id} x={Math.min(sh.x1, sh.x2)} y={Math.min(sh.y1, sh.y2)} width={Math.abs(sh.x2 - sh.x1)} height={Math.abs(sh.y2 - sh.y1)} fill="rgba(192,57,43,0.18)" stroke="#C0392B" strokeWidth={2} />;
            if (sh.tool === "room") return <g key={sh.id}><rect x={sh.x} y={sh.y} width={sh.w} height={sh.h} fill="#fff" stroke="#1a1a1a" strokeWidth={2} /><text x={sh.x + sh.w / 2} y={sh.y + sh.h / 2} fill="#1a1a1a" textAnchor="middle" fontSize="13" fontWeight="700">{sh.label}</text></g>;
            if (sh.tool === "label") return <text key={sh.id} x={sh.x} y={sh.y} fill="#1a1a1a" fontSize="14" fontWeight="700">{sh.text}</text>;
            if (["airmover", "dehu", "scrubber", "moisture"].includes(sh.tool)) return <g key={sh.id}><circle cx={sh.x} cy={sh.y} r={18} fill="#fff" stroke={sh.tool === "moisture" ? "#1e40af" : "#C0392B"} strokeWidth={2} /><text x={sh.x} y={sh.y + 5} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1a1a1a">{icons[sh.tool]}</text></g>;
            return null;
          })}
          {drawing && drawing.tool === "wall" && <line x1={drawing.x1} y1={drawing.y1} x2={drawing.x2} y2={drawing.y2} stroke="#1a1a1a" strokeOpacity="0.5" strokeWidth={4} />}
          {drawing && drawing.tool === "dim" && <line x1={drawing.x1} y1={drawing.y1} x2={drawing.x2} y2={drawing.y2} stroke="#1e40af" strokeOpacity="0.5" strokeDasharray="6 4" />}
          {drawing && drawing.tool === "affected" && <rect x={Math.min(drawing.x1, drawing.x2)} y={Math.min(drawing.y1, drawing.y2)} width={Math.abs(drawing.x2 - drawing.x1)} height={Math.abs(drawing.y2 - drawing.y1)} fill="rgba(192,57,43,0.1)" stroke="#C0392B" />}
        </svg>
      </div>
      <div style={{ fontSize: 10, color: "#666", marginTop: 6, fontWeight: 600 }}>
        Equipment placed on sketch links to the Equipment Log in Dry Readings. Use "Save Sketch" then export the drying report to include this plan.
      </div>
    </div>
  );
}
const btn3 = { fontSize: 10, padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 3, cursor: "pointer", fontWeight: 600, background: "#fff", color: "#1a1a1a" };

// ─── Water Category & Class fields (FIX 7) ───────────────────────────────
export function WaterCategoryClassFields({ jobId, initial, onChange }) {
  const [cat, setCat] = useState(initial?.water_category || "");
  const [cls, setCls] = useState(initial?.water_class || "");
  const [sqft, setSqft] = useState(initial?.affected_sq_ft || "");
  const save = async (patch) => {
    try {
      const payload = { job_id: jobId, water_category: patch.water_category ?? cat, water_class: patch.water_class ?? cls, affected_sq_ft: patch.affected_sq_ft ?? (parseFloat(sqft) || null) };
      await j("/api/iv/r14/jobs/water", { method: "PUT", body: JSON.stringify(payload) });
      toast("Saved");
      onChange && onChange(payload);
    } catch (e) { toast(e.message, "err"); }
  };
  return (
    <div data-testid="r14-water-cat-class" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fafafa", margin: "8px 0" }}>
      <Field label="Water Category">
        <select style={inDark} value={cat} onChange={e => { setCat(e.target.value); save({ water_category: e.target.value }); }} data-testid="r14-water-cat">
          <option value="">— Select —</option>
          <option value="Category 1">Category 1 — Clean Water</option>
          <option value="Category 2">Category 2 — Grey Water</option>
          <option value="Category 3">Category 3 — Black Water</option>
        </select>
      </Field>
      <Field label="Water Class">
        <select style={inDark} value={cls} onChange={e => { setCls(e.target.value); save({ water_class: e.target.value }); }} data-testid="r14-water-cls">
          <option value="">— Select —</option>
          <option value="Class 1">Class 1 — Slow evaporation</option>
          <option value="Class 2">Class 2 — Fast evaporation</option>
          <option value="Class 3">Class 3 — Fastest evaporation</option>
          <option value="Class 4">Class 4 — Specialty drying</option>
        </select>
      </Field>
      <Field label="Affected Sq Ft">
        <input style={inDark} type="number" value={sqft} onChange={e => setSqft(e.target.value)} onBlur={() => save({ affected_sq_ft: parseFloat(sqft) || null })} data-testid="r14-water-sqft" />
      </Field>
    </div>
  );
}

// ─── BRANDING (FIX 9, completed in R15) ───────────────────────────────────
const THEME_PRESETS = {
  light: { "--bg": "#ffffff", "--bg2": "#f5f5f5", "--bg3": "#fafafa", "--text": "#1a1a1a", "--text2": "#2c2c2c", "--text3": "#555555", "--border": "#e5e7eb", "--border2": "#d1d5db", "_body_bg": "#ffffff" },
  grey:  { "--bg": "#c0c5cb", "--bg2": "#d8dde2", "--bg3": "#e8ecf0", "--text": "#111111", "--text2": "#2c2c2c", "--text3": "#5c6166", "--border": "#a8aeb4", "--border2": "#8e949a", "_body_bg": "#c0c5cb" },
  dark:  { "--bg": "#0b1220", "--bg2": "#111827", "--bg3": "#1f2937", "--text": "#f9fafb", "--text2": "#e5e7eb", "--text3": "#9ca3af", "--border": "#374151", "--border2": "#4b5563", "_body_bg": "#0b1220" },
};

// Exported so App.js / main bootstrap can call it on mount to apply persisted theme.
export function applyBranding(b) {
  if (!b || typeof window === "undefined") return;
  const root = document.documentElement;
  const preset = b.use_custom_colors ? null : THEME_PRESETS[b.theme || "grey"];
  // Build the effective var map
  const map = { ...(preset || {}) };
  if (b.use_custom_colors) {
    if (b.bg_main)    { map["--bg"]  = b.bg_main;    map._body_bg = b.bg_main; }
    if (b.bg_sidebar) { map["--bg2"] = b.bg_sidebar; }
    if (b.bg_card)    { map["--bg3"] = b.bg_card; }
    if (b.text_color) { map["--text"] = b.text_color; map["--text2"] = b.text_color; }
  }
  if (b.accent_color) { map["--cyan"] = b.accent_color; map["--red"] = b.accent_color; }
  Object.entries(map).forEach(([k, v]) => { if (k === "_body_bg") return; if (v) root.style.setProperty(k, v); });
  if (map._body_bg) document.body.style.background = map._body_bg;
  // Override-stylesheet so previously-baked colour values in inline styles get re-skinned
  let styleEl = document.getElementById("r15-branding-overrides");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "r15-branding-overrides";
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    body, .app { background: var(--bg) !important; color: var(--text) !important; }
    .sidebar { background: var(--bg2) !important; color: var(--text) !important; }
    .topbar, .card, .card-header, .kpi, .modal { background: var(--bg2) !important; color: var(--text) !important; }
    .card-body, .form-control, table thead { color: var(--text) !important; }
    .btn-primary { background: var(--cyan) !important; color: #fff !important; }
    .nav-item, .nav-section { color: var(--text2) !important; }
  `;
  try { localStorage.setItem("apex_branding_cache", JSON.stringify(b)); } catch {}
}

// Bootstrap: on first import, hydrate from localStorage so the chosen theme survives a refresh.
(function bootstrapBranding() {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem("apex_branding_cache");
    if (raw) applyBranding(JSON.parse(raw));
  } catch {}
  // Then re-sync from server in the background (per-company branding).
  setTimeout(() => {
    fetch(`${API}/api/iv/r14/branding`, { headers: { Authorization: "Bearer " + tok() } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.branding && Object.keys(d.branding).length) applyBranding(d.branding); })
      .catch(() => {});
  }, 1500);
})();

export function BrandingPanel({ user }) {
  // OWNER LOCK — only the owner sees / edits branding. Others get a gentle disabled notice.
  const isOwner = (user?.role || "").toLowerCase() === "owner";
  const [data, setData] = useState({ theme: "grey", accent_color: "#C0392B", use_custom_colors: false, logo_data_uri: "", bg_main: "", bg_sidebar: "", bg_card: "", text_color: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { j(`/api/iv/r14/branding`).then(d => { if (d.branding && Object.keys(d.branding).length) { setData(prev => ({ ...prev, ...d.branding })); applyBranding(d.branding); } }).catch(() => {}); }, []);
  const upd = (k, v) => { if (!isOwner) return; setData(d => { const next = { ...d, [k]: v }; applyBranding(next); return next; }); };
  const save = async () => {
    if (!isOwner) return;
    setBusy(true);
    try { await j(`/api/iv/r14/branding`, { method: "PUT", body: JSON.stringify(data) }); toast("Branding saved · applies to all users in company"); applyBranding(data); }
    catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  };
  const reset = () => {
    if (!isOwner) return;
    const def = { theme: "grey", accent_color: "#C0392B", use_custom_colors: false, logo_data_uri: "", bg_main: "", bg_sidebar: "", bg_card: "", text_color: "" };
    setData(def); applyBranding(def);
  };
  const onLogo = async (e) => {
    if (!isOwner) return;
    const f = (e.target.files || [])[0]; if (!f) return;
    const b64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(f); });
    upd("logo_data_uri", b64);
  };
  const themes = [
    { key: "light", label: "Light Theme", bg: "#fff", side: "#f5f5f5", card: "#fff", text: "#1a1a1a", note: "White backgrounds · dark grey text · red accents" },
    { key: "grey",  label: "Grey Theme (default)", bg: "#e8e8e8", side: "#d8d8d8", card: "#fff", text: "#1a1a1a", note: "Silver grey · dark text · red accents" },
    { key: "dark",  label: "Dark Theme", bg: "#111827", side: "#0b1220", card: "#1f2937", text: "#fff", note: "Dark navy · white text · red accents" },
  ];
  const isLight = (hex) => { if (!hex || hex.length < 4) return true; const h = hex.replace("#",""); const r = parseInt(h.slice(0,2),16), g = parseInt(h.slice(2,4),16), b = parseInt(h.slice(4,6),16); return (0.299*r + 0.587*g + 0.114*b) > 160; };
  const clash = data.use_custom_colors && data.bg_card && data.text_color && (isLight(data.bg_card) === isLight(data.text_color));

  if (!isOwner) {
    return (
      <div data-testid="r14-branding-locked" style={{ padding: 16, color: "#1a1a1a" }}>
        <h2 style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 18, fontWeight: 700, color: "#1a1a1a" }}>Branding</h2>
        <div style={{ padding: "14px 16px", background: "#fafafa", border: "1px dashed #d1d5db", borderRadius: 6, fontSize: 12, color: "#555", fontWeight: 600 }}>
          Branding (logo, themes, custom colors) is set company-wide by the owner / master account.
          The current company branding applies automatically to your sign-in.
        </div>
      </div>
    );
  }
  // Disabled visuals (owner-only edits)
  const dis = { pointerEvents: "auto", opacity: 1 };
  return (
    <div data-testid="r14-branding" style={{ padding: 16, color: "#1a1a1a", ...dis }}>
      <h2 style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 18, fontWeight: 700, color: "#1a1a1a" }}>Branding</h2>
      <div style={{ padding: "8px 10px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 4, fontSize: 11, fontWeight: 600, color: "#1a1a1a", marginBottom: 12 }}>
        Owner-only · saved at the company level · applies to every user in this company automatically.
      </div>
      <div style={{ padding: "12px 14px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", marginBottom: 6 }}>Company Logo</div>
        <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onLogo} data-testid="r14-brand-logo" style={{ fontSize: 11 }} />
        <div style={{ fontSize: 10, color: "#666", fontWeight: 600, marginTop: 4 }}>Recommended size: 300×100px · PNG/JPG/SVG. Appears in header & all PDF exports.</div>
        {data.logo_data_uri && <div style={{ marginTop: 8, padding: 8, background: "#f9fafb", borderRadius: 4, border: "1px solid #e5e7eb" }}><div style={{ fontSize: 10, fontWeight: 600, color: "#1a1a1a", marginBottom: 4 }}>Header preview:</div><img src={data.logo_data_uri} alt="logo" style={{ maxHeight: 50 }} /></div>}
      </div>

      <div style={{ padding: "12px 14px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", marginBottom: 6 }}>Dashboard Theme</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {themes.map(t => (
            <div key={t.key} data-testid={`r14-brand-theme-${t.key}`} onClick={() => !data.use_custom_colors && upd("theme", t.key)}
              style={{ cursor: data.use_custom_colors ? "not-allowed" : "pointer", opacity: data.use_custom_colors ? 0.4 : 1,
                       padding: 10, border: data.theme === t.key && !data.use_custom_colors ? "2px solid #C0392B" : "1px solid #d1d5db", borderRadius: 6, width: 200 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", marginBottom: 6 }}>{t.label}</div>
              <div style={{ background: t.bg, padding: 6, borderRadius: 4 }}>
                <div style={{ background: t.side, padding: 6, borderRadius: 3, marginBottom: 4 }}><div style={{ fontSize: 9, color: t.text }}>SIDEBAR</div></div>
                <div style={{ background: t.card, padding: 6, borderRadius: 3 }}><div style={{ fontSize: 9, color: t.text }}>{t.note}</div></div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 10, color: "#666", fontWeight: 600, marginTop: 6 }}>Click a card to preview live. Click <strong>Save Branding</strong> below to lock it in for everyone.</div>
      </div>

      <div style={{ padding: "12px 14px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", marginBottom: 6 }}>Primary Accent Color</div>
        <input type="color" value={data.accent_color} onChange={e => upd("accent_color", e.target.value)} data-testid="r14-brand-accent" />
        <input value={data.accent_color} onChange={e => upd("accent_color", e.target.value)} style={{ ...inDark, marginLeft: 8, width: 100, display: "inline-block" }} />
        <div style={{ fontSize: 10, color: "#666", fontWeight: 600, marginTop: 4 }}>Applies to buttons, badges, highlights. Default Apex red #C0392B.</div>
      </div>

      <div style={{ padding: "12px 14px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", marginBottom: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#1a1a1a" }}>
          <input type="checkbox" checked={!!data.use_custom_colors} onChange={e => upd("use_custom_colors", e.target.checked)} data-testid="r14-brand-custom-toggle" />
          Use Custom Colors (disables preset themes)
        </label>
        {data.use_custom_colors && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
            {[["Main Background", "bg_main"], ["Sidebar Background", "bg_sidebar"], ["Card / Panel Background", "bg_card"], ["Text Color", "text_color"]].map(([lbl, k]) => (
              <div key={k}>
                <div style={lblDark}>{lbl}</div>
                <input type="color" value={data[k] || "#ffffff"} onChange={e => upd(k, e.target.value)} data-testid={`r14-brand-${k}`} />
                <input value={data[k] || ""} onChange={e => upd(k, e.target.value)} style={{ ...inDark, marginLeft: 8, width: 100, display: "inline-block" }} placeholder="#hex" />
              </div>
            ))}
            <div style={{ gridColumn: "1/-1", padding: 10, background: data.bg_card || "#fff", color: data.text_color || "#1a1a1a", borderRadius: 4, border: "1px solid #d1d5db", marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700 }}>Live preview</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>This is how text and cards will look with your chosen colors.</div>
            </div>
            {clash && <div style={{ gridColumn: "1/-1", padding: 8, background: "#fef3c7", border: "1px solid #fcd34d", color: "#7c2d12", fontSize: 11, fontWeight: 700, borderRadius: 4 }}>⚠ This color may make text hard to read. Consider adjusting Text Color above.</div>}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={save} disabled={busy} className="btn btn-primary" style={{ fontSize: 12 }} data-testid="r14-brand-save">{busy ? "Saving…" : "Save Branding"}</button>
        <button onClick={reset} style={{ fontSize: 12, padding: "6px 12px", background: "#fff", border: "1px solid #d1d5db", color: "#1a1a1a", fontWeight: 600, borderRadius: 4, cursor: "pointer" }} data-testid="r14-brand-reset">Reset to Default</button>
      </div>
    </div>
  );
}

function applyTheme(d) { applyBranding(d); }

// ─── EQUIPMENT MANAGER (FIX 10) ───────────────────────────────────────────
export function EquipmentR14() {
  const [data, setData] = useState({ equipment: [], by_category: {}, categories: [] });
  const [filterCat, setFilterCat] = useState("");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({ category: "Air Movers / Axial Fans", name: "", make_model: "", serial: "", quantity: 1, daily_rate: "", condition: "Good", status: "Available", notes: "" });
  const load = useCallback(() => {
    const qs = new URLSearchParams(); if (filterCat) qs.set("category", filterCat); if (q) qs.set("q", q);
    j(`/api/iv/r14/equipment?${qs.toString()}`).then(setData).catch(() => {});
  }, [filterCat, q]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await j(`/api/iv/r14/equipment`, { method: "POST", body: JSON.stringify({ ...draft, daily_rate: draft.daily_rate === "" ? null : parseFloat(draft.daily_rate) }) }); toast("Equipment saved"); setShowAdd(false); setDraft({ category: "Air Movers / Axial Fans", name: "", make_model: "", serial: "", quantity: 1, daily_rate: "", condition: "Good", status: "Available", notes: "" }); load(); }
    catch (e) { toast(e.message, "err"); }
  };
  const STATUSES = ["Active", "Deployed", "Available", "Needs Repair", "In Repair", "Out of Service", "Lost / Missing"];
  const updStatus = async (eq, status) => {
    try { await j(`/api/iv/r14/equipment`, { method: "POST", body: JSON.stringify({ ...eq, status }) }); load(); }
    catch (e) { toast(e.message, "err"); }
  };
  const flag = (s) => ["Needs Repair", "In Repair", "Lost / Missing"].includes(s);

  return (
    <div data-testid="r14-equipment" style={{ padding: 16, color: "#1a1a1a" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <h2 style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 18, fontWeight: 700, color: "#1a1a1a", margin: 0 }}>Equipment</h2>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search equipment…" style={{ ...inDark, width: 240 }} data-testid="r14-eq-search" />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...inDark, width: 220 }} data-testid="r14-eq-cat-filter">
          <option value="">All categories</option>
          {(data.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary" style={{ fontSize: 11 }} data-testid="r14-eq-add">+ Add Equipment</button>
      </div>
      {(data.categories || []).map(cat => {
        const items = (data.by_category || {})[cat] || [];
        if (filterCat && cat !== filterCat) return null;
        return (
          <details key={cat} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", marginBottom: 6, background: "#fff" }} open>
            <summary data-testid={`r14-eq-cat-${cat}`} style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", cursor: "pointer" }}>{cat} <span style={{ fontSize: 10, color: "#666", marginLeft: 6 }}>({items.length})</span></summary>
            {items.length === 0 && <div style={{ fontSize: 10, color: "#666", marginTop: 6 }}>No items in this category.</div>}
            {items.length > 0 && (
              <table style={{ width: "100%", fontSize: 11, marginTop: 6 }}>
                <thead><tr><th style={th}>Name</th><th style={th}>Make/Model</th><th style={th}>Serial</th><th style={th}>Qty</th><th style={th}>Status</th><th style={th}>Repair Info</th></tr></thead>
                <tbody>
                  {items.map(eq => (
                    <tr key={eq.id} style={flag(eq.status) ? { background: "#fee2e2" } : {}}>
                      <td style={td}>{eq.name}</td>
                      <td style={td}>{eq.make_model}</td>
                      <td style={td}>{eq.serial}</td>
                      <td style={td}>{eq.quantity}</td>
                      <td style={td}>
                        <select value={eq.status} onChange={e => updStatus(eq, e.target.value)} style={{ ...inDark, padding: "3px 6px", fontWeight: 700, color: flag(eq.status) ? "#7f1d1d" : "#1a1a1a" }} data-testid={`r14-eq-status-${eq.id}`}>
                          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={td}>{flag(eq.status) ? <span style={{ fontSize: 10, color: "#7f1d1d", fontWeight: 700 }}>{eq.repair_notes || "—"}{eq.estimated_return ? ` · ETA ${eq.estimated_return}` : ""}</span> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </details>
        );
      })}
      {showAdd && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 8000 }} data-testid="r14-eq-add-modal">
          <div style={{ background: "#fff", borderRadius: 8, padding: 16, width: 480, color: "#1a1a1a" }}>
            <div style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Add Equipment</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <Field label="Category">
                <select style={inDark} value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })} data-testid="r14-eq-form-cat">
                  {(data.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Equipment Name"><input style={inDark} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} data-testid="r14-eq-form-name" /></Field>
              <Field label="Make / Model"><input style={inDark} value={draft.make_model} onChange={e => setDraft({ ...draft, make_model: e.target.value })} /></Field>
              <Field label="Serial"><input style={inDark} value={draft.serial} onChange={e => setDraft({ ...draft, serial: e.target.value })} /></Field>
              <Field label="Quantity"><input style={inDark} type="number" value={draft.quantity} onChange={e => setDraft({ ...draft, quantity: parseInt(e.target.value) || 1 })} /></Field>
              <Field label="Daily Rate $"><input style={inDark} type="number" value={draft.daily_rate} onChange={e => setDraft({ ...draft, daily_rate: e.target.value })} /></Field>
              <Field label="Condition">
                <select style={inDark} value={draft.condition} onChange={e => setDraft({ ...draft, condition: e.target.value })}>
                  {["Excellent", "Good", "Fair", "Needs Repair", "Out of Service"].map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select style={inDark} value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })}>
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Notes"><textarea style={{ ...inDark, minHeight: 50, fontFamily: "inherit" }} value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} /></Field>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
              <button onClick={() => setShowAdd(false)} style={{ fontSize: 11, padding: "6px 12px", background: "transparent", border: "1px solid #d1d5db", color: "#1a1a1a", fontWeight: 600, borderRadius: 4, cursor: "pointer" }}>Cancel</button>
              <button onClick={save} className="btn btn-primary" style={{ fontSize: 11 }} data-testid="r14-eq-form-save">Save Equipment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── R16 — LINKED JOBS (sub-jobs by trade category) ─────────────────────
export function LinkedJobsBar({ masterId, currentJobId, onPickJob, onCreated, onDeleted }) {
  const [subs, setSubs] = useState([]);
  const [cats, setCats] = useState([]);
  const [pickCat, setPickCat] = useState("Water");
  const [customAbbr, setCustomAbbr] = useState("");
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);   // sub-job pending confirm
  const load = useCallback(() => {
    j(`/api/iv/r14/links/${masterId}`).then(d => { setSubs(d.sub_jobs || []); setCats(d.categories || []); }).catch(() => setSubs([]));
  }, [masterId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (pickCat === "Custom" && !customAbbr.trim()) { toast("Enter a custom code", "err"); return; }
    // R17 FIX 3 — duplicate-trade prompt (don't silently create J-XXXX-WTR-2)
    const ABBR_BY_CAT = { Water:"WTR", Mold:"MOLD", "Fire / Smoke":"FIRE", Demo:"DEMO", "Reconstruction / Repairs":"REP", "Storm / CAT":"CAT", Biohazard:"BIO", Contents:"CON", Roofing:"ROOF", Mitigation:"MIT", Inspection:"INS" };
    const wantAbbr = pickCat === "Custom" ? customAbbr.trim().toUpperCase() : ABBR_BY_CAT[pickCat];
    const dupes = subs.filter(s => (s.linked_abbr || "").toUpperCase() === wantAbbr);
    if (dupes.length > 0) {
      const label = pickCat === "Custom" ? wantAbbr : pickCat;
      const ok = window.confirm(`A ${label} job already exists for this master job. Add another anyway?`);
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await j(`/api/iv/r14/sublink`, { method: "POST", body: JSON.stringify({ master_id: masterId, category: pickCat, custom_abbr: customAbbr }) });
      toast(`Linked job created: ${r.sub_job.id}`);
      setCustomAbbr("");
      load();
      onCreated && onCreated(r.sub_job);
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  };
  const confirmDelete = async (subId) => {
    setBusy(true);
    try {
      const r = await j(`/api/iv/r14/sublink/${encodeURIComponent(subId)}`, { method: "DELETE" });
      toast(`Deleted ${subId}`);
      setDeletingId(null);
      // After deletion, jump back to the master/Overview view
      onPickJob && onPickJob(masterId);
      onDeleted && onDeleted(subId, r.cascade || {});
      load();
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`r16-linked-bar-${masterId}`} style={{ background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", margin: "8px 0" }}>
      {/* Tabs row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: subs.length ? 8 : 0 }}>
        <button data-testid={`r16-tab-${masterId}`} onClick={() => onPickJob(masterId)} style={tabStyle(currentJobId === masterId)}>
          {masterId} — Overview
        </button>
        {subs.map(s => (
          <span key={s.id} style={{ display: "inline-flex", alignItems: "stretch" }}>
            <button data-testid={`r16-tab-${s.id}`} onClick={() => onPickJob(s.id)} style={{ ...tabStyle(currentJobId === s.id), borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: "none" }}>
              {s.id} {s.linked_category}
            </button>
            <button
              data-testid={`r16-del-${s.id}`}
              title={`Delete ${s.id}`}
              onClick={(e) => { e.stopPropagation(); setDeletingId(s.id); }}
              style={{
                ...tabStyle(currentJobId === s.id),
                borderTopLeftRadius: 0, borderBottomLeftRadius: 0,
                padding: "5px 8px", fontWeight: 800,
              }}
            >×</button>
          </span>
        ))}
      </div>
      {/* Add row */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#1a1a1a", letterSpacing: 1 }}>+ ADD LINKED JOB:</span>
        <select value={pickCat} onChange={e => setPickCat(e.target.value)} data-testid={`r16-cat-${masterId}`} style={{ ...inDark, width: 200, fontSize: 11 }}>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {pickCat === "Custom" && <input data-testid={`r16-custom-${masterId}`} value={customAbbr} onChange={e => setCustomAbbr(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))} placeholder="1-4 letter code" maxLength={4} style={{ ...inDark, width: 130 }} />}
        <button onClick={create} disabled={busy} className="btn btn-primary" style={{ fontSize: 11 }} data-testid={`r16-add-${masterId}`}>{busy ? "…" : "+ Add Linked Job"}</button>
        {subs.length > 0 && <span style={{ fontSize: 10, color: "#666", fontWeight: 600, marginLeft: "auto" }}>{subs.length} linked sub-job{subs.length === 1 ? "" : "s"} · shared customer & claim data</span>}
      </div>

      {/* Delete confirmation modal */}
      {deletingId && (
        <div data-testid="r16-delete-confirm" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={() => !busy && setDeletingId(null)}>
          <div style={{ background: "#fff", borderRadius: 8, padding: 24, maxWidth: 460, width: "92%", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1a1a1a", marginBottom: 10 }}>Delete {deletingId}?</div>
            <div style={{ fontSize: 13, color: "#444", marginBottom: 18, lineHeight: 1.4 }}>
              This permanently removes this linked job and all its data
              (dry readings, repairs, photos, sketch, equipment log, notes, invoice).
              <br /><strong>This cannot be undone.</strong>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button data-testid="r16-delete-cancel" onClick={() => setDeletingId(null)} disabled={busy} style={{ padding: "6px 14px", fontSize: 12, fontWeight: 700, background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}>Cancel</button>
              <button data-testid="r16-delete-confirm-btn" onClick={() => confirmDelete(deletingId)} disabled={busy} style={{ padding: "6px 14px", fontSize: 12, fontWeight: 700, background: "#C0392B", color: "#fff", border: "1px solid #C0392B", borderRadius: 4, cursor: "pointer" }}>{busy ? "Deleting…" : "Delete permanently"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const tabStyle = (active) => ({
  padding: "5px 12px", fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
  border: "1px solid " + (active ? "#C0392B" : "#d1d5db"), borderRadius: 4, cursor: "pointer",
  background: active ? "#C0392B" : "#fff",
  color: active ? "#fff" : "#1a1a1a",
});

// Hook: pull sub-jobs of any given master so the Job Management list can group them visually
export function useLinkedSubs(masterId) {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    if (!masterId) return;
    j(`/api/iv/r14/links/${masterId}`).then(d => setSubs(d.sub_jobs || [])).catch(() => setSubs([]));
  }, [masterId]);
  return subs;
}

// Propagate shared field change to master + all siblings
export async function propagateSharedField(jobId, patch) {
  try { await j(`/api/iv/r14/links/shared`, { method: "PUT", body: JSON.stringify({ job_id: jobId, patch }) }); }
  catch (e) { /* swallow */ }
}
